import { Convert, MyDataUploadResponse, MyDataUploadResponseStatus } from "@/models/responses";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/lib/shadcn/ui/dialog";
import { Button } from "@/lib/shadcn/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/lib/shadcn/ui/table";
import { AlertTriangle, CheckCheck, CheckCircle2, Loader2, XCircle } from "lucide-react";
import { apiUrl } from "@/layouts/apiUrl";

interface UploadToMyDataModalProps {
    /** Whether the modal is open. */
    open: boolean;
    /** Reservation ids to upload. Empty → defensive "no reservations" state. */
    reservationIds: number[];
    /** Called when the dialog is dismissed (Close/backdrop/Escape, post-resolution). */
    onClose: () => void;
    /** Fired once results have arrived so the caller can refresh the grid. */
    onComplete: () => void;
}

type Phase = "loading" | "result" | "error" | "empty";

const RESENT_WARNING =
    "Needs-reconciliation rows may already be filed at MyData. Do NOT resend them — verify on reporting.cloudhotel.gr first.";

const RECONCILIATION_DETAIL =
    "Attempted but unconfirmed — may already be filed at MyData. Verify on reporting.cloudhotel.gr before any resend.";

const ERROR_LEAD = "Upload could not start — no reservations were sent to MyData. Nothing was filed.";

interface StatusPresentation {
    label: string;
    Icon: React.FC<{ className?: string; role?: string; "aria-label"?: string; title?: string }>;
    iconClass: string;
    rowClass: string;
}

const STATUS_PRESENTATION: Record<MyDataUploadResponseStatus, StatusPresentation> = {
    [MyDataUploadResponseStatus.Filed]: {
        label: "Filed",
        Icon: CheckCircle2,
        // green-700 ≈ 4.5:1 on white — clears WCAG 1.4.11 (green-500 was 2.28:1).
        iconClass: "text-green-700",
        rowClass: "",
    },
    [MyDataUploadResponseStatus.Failed]: {
        label: "Failed",
        Icon: XCircle,
        iconClass: "text-red-500",
        rowClass: "",
    },
    [MyDataUploadResponseStatus.AlreadyFiled]: {
        label: "Already filed",
        Icon: CheckCheck,
        // slate-500 ≈ 3.6:1 — clears WCAG 1.4.11 (slate-400 was 2.56:1).
        iconClass: "text-slate-500",
        // De-emphasis row tint kept, but slate-500 (≈3.6:1) not slate-400.
        // The readable status label opts out of this via its own slate-600
        // so the text label clears WCAG 1.4.3 (≥4.5:1) — see StatusCell.
        rowClass: "text-slate-500",
    },
    [MyDataUploadResponseStatus.NeedsReconciliation]: {
        label: "Needs reconciliation",
        Icon: AlertTriangle,
        iconClass: "text-amber-600",
        rowClass: "bg-amber-50",
    },
};

function countByStatus(rows: MyDataUploadResponse[], status: MyDataUploadResponseStatus): number {
    return rows.filter((r) => r.status === status).length;
}

const InvoiceLink: React.FC<{ uuid: string; label: string }> = ({ uuid, label }) => (
    <a
        href={`https://reporting.cloudhotel.gr/public/invoice/${uuid}`}
        className="text-blue-600 hover:text-blue-800 hover:underline"
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
    >
        Link
    </a>
);

const StatusCell: React.FC<{ result: MyDataUploadResponse }> = ({ result }) => {
    const presentation = STATUS_PRESENTATION[result.status];
    const { label, Icon } = presentation;
    return (
        <div className="flex items-center gap-2">
            <Icon className={`h-5 w-5 ${presentation.iconClass}`} role="img" aria-label={label} title={label} />
            {/* Explicit slate-600 (≈7.6:1) so the status label never inherits a
                muted rowClass (e.g. already-filed's slate-500) and always
                clears WCAG 1.4.3 text contrast. */}
            <span className="whitespace-nowrap text-slate-600">{label}</span>
        </div>
    );
};

const ResultRow: React.FC<{ result: MyDataUploadResponse }> = ({ result }) => {
    const presentation = STATUS_PRESENTATION[result.status];
    const code = result.reservationCode;
    const isFiledLike =
        result.status === MyDataUploadResponseStatus.Filed ||
        result.status === MyDataUploadResponseStatus.AlreadyFiled;
    const isReconciliation = result.status === MyDataUploadResponseStatus.NeedsReconciliation;

    const errorText = isReconciliation
        ? RECONCILIATION_DETAIL
        : result.status === MyDataUploadResponseStatus.Failed
          ? result.error
          : "";

    return (
        <TableRow data-testid={`mydata-row-${code}`} className={presentation.rowClass}>
            <TableCell className="font-medium">{code}</TableCell>
            <TableCell data-testid={`mydata-status-${code}`} data-status={result.status}>
                <StatusCell result={result} />
            </TableCell>
            <TableCell>
                {isFiledLike && result.taxInvoiceUUID ? (
                    <InvoiceLink uuid={result.taxInvoiceUUID} label={`Tax invoice for ${code}, opens in new tab`} />
                ) : (
                    <span aria-hidden="true">—</span>
                )}
            </TableCell>
            <TableCell>
                {isFiledLike && result.invoiceUUID ? (
                    <InvoiceLink
                        uuid={result.invoiceUUID}
                        label={`Reservation invoice for ${code}, opens in new tab`}
                    />
                ) : (
                    <span aria-hidden="true">—</span>
                )}
            </TableCell>
            <TableCell className="wrap-break-word">{errorText}</TableCell>
        </TableRow>
    );
};

const SummaryBanner: React.FC<{ rows: MyDataUploadResponse[] }> = ({ rows }) => {
    const filed = countByStatus(rows, MyDataUploadResponseStatus.Filed);
    const failed = countByStatus(rows, MyDataUploadResponseStatus.Failed);
    const reconciliation = countByStatus(rows, MyDataUploadResponseStatus.NeedsReconciliation);
    const alreadyFiled = countByStatus(rows, MyDataUploadResponseStatus.AlreadyFiled);

    return (
        <div data-testid="mydata-summary" aria-live="polite" className="space-y-1 text-sm">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="text-green-700">{filed} filed</span>
                <span aria-hidden="true">·</span>
                <span className="text-red-600">{failed} failed</span>
                <span aria-hidden="true">·</span>
                <span className={reconciliation > 0 ? "text-amber-700 font-medium" : "text-slate-500"}>
                    {reconciliation} needs reconciliation
                </span>
                <span aria-hidden="true">·</span>
                <span className="text-slate-500">{alreadyFiled} already filed</span>
            </div>
            {reconciliation > 0 && (
                <p className="flex items-start gap-1 text-amber-700">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                    <span>{RESENT_WARNING}</span>
                </p>
            )}
        </div>
    );
};

const UploadToMydataDialog: React.FC<UploadToMyDataModalProps> = ({
    open,
    reservationIds,
    onClose,
    onComplete,
}) => {
    const [phase, setPhase] = useState<Phase>("loading");
    const [results, setResults] = useState<MyDataUploadResponse[]>([]);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const triggerRef = useRef<Element | null>(null);
    const requestIdRef = useRef(0);

    const isEmpty = reservationIds.length === 0;

    const runUpload = useCallback(() => {
        const requestId = ++requestIdRef.current;
        setPhase("loading");
        fetch(`${apiUrl}/api/admin/reservations/mydata-upload`, {
            method: "POST",
            body: JSON.stringify({ reservationIds }),
            credentials: "include",
        })
            .then(async (response) => {
                // A non-2xx body is NOT a MyDataUploadResponse — never feed it
                // to Convert (the old code did, which threw and hung the
                // dialog with no content). Extract a human message instead.
                if (!response.ok) {
                    let reason = `HTTP ${response.status}`;
                    try {
                        const text = await response.text();
                        const parsed = JSON.parse(text);
                        if (parsed?.errors?.length) {
                            reason = parsed.errors
                                .map((e: { message: string }) => e.message)
                                .join(", ");
                        }
                    } catch {
                        // Keep the HTTP-status fallback.
                    }
                    if (requestId !== requestIdRef.current) return;
                    setErrorMessage(reason);
                    setPhase("error");
                    return;
                }
                const text = await response.text();
                if (requestId !== requestIdRef.current) return;
                const rows = Convert.toUploadToMyDataResponse(text).myDataUploadResponse;
                setResults(rows);
                setPhase("result");
                onComplete();
            })
            .catch((err: unknown) => {
                if (requestId !== requestIdRef.current) return;
                setErrorMessage(err instanceof Error ? err.message : "Network error");
                setPhase("error");
            });
    }, [reservationIds, onComplete]);

    // Kick off (or skip) the upload whenever the dialog opens.
    useEffect(() => {
        if (!open) return;
        // Capture the element that had focus so we can restore it on close.
        triggerRef.current = document.activeElement;
        if (isEmpty) {
            setPhase("empty");
            return;
        }
        runUpload();
        // Intentionally keyed on `open` only: re-running on every `runUpload`
        // identity change would re-fire the request mid-flight.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    // Loading must never collapse into a frozen, contentless dialog: block
    // every dismissal path while the request is in flight.
    const handleOpenChange = useCallback(
        (next: boolean) => {
            if (next) return;
            if (phase === "loading") return;
            handleClose();
        },
        [phase, handleClose],
    );

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                className="sm:max-w-6xl"
                showCloseButton={false}
                onEscapeKeyDown={(e) => {
                    if (phase === "loading") {
                        e.preventDefault();
                    }
                }}
                onInteractOutside={(e) => {
                    if (phase === "loading") {
                        e.preventDefault();
                    }
                }}
                onCloseAutoFocus={(e) => {
                    // Return focus to the control that opened the dialog
                    // rather than letting Radix drop it on <body>.
                    const el = triggerRef.current;
                    if (el instanceof HTMLElement) {
                        e.preventDefault();
                        el.focus();
                    }
                }}
            >
                <DialogHeader>
                    <DialogTitle>Upload to MyData</DialogTitle>
                    <DialogDescription className="sr-only">
                        Per-reservation result of the MyData invoice upload.
                    </DialogDescription>
                </DialogHeader>

                {phase === "loading" && (
                    <div className="flex flex-col items-center gap-3 py-10" role="status" aria-live="polite">
                        <Loader2 className="h-8 w-8 animate-spin text-slate-500" aria-hidden="true" />
                        <p className="text-sm text-slate-600">
                            Uploading {reservationIds.length}{" "}
                            {reservationIds.length === 1 ? "reservation" : "reservations"} to MyData…
                        </p>
                        {/* Escape/backdrop are intentionally inert while
                            loading (the dialog must never freeze contentless).
                            Announce that the wait is bounded so keyboard/SR
                            users know it self-resolves — WCAG 2.1.2. */}
                        <p className="text-sm text-slate-600">
                            Please wait — this dialog will close automatically when the upload finishes.
                        </p>
                    </div>
                )}

                {phase === "empty" && (
                    <div className="flex flex-col items-center gap-4 py-10">
                        <p className="text-sm text-slate-600">No reservations selected.</p>
                        <Button variant="outline" onClick={handleClose}>
                            Close
                        </Button>
                    </div>
                )}

                {phase === "error" && (
                    <div
                        data-testid="mydata-error-panel"
                        role="alert"
                        className="flex flex-col items-center gap-4 py-8 text-center"
                    >
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-600">
                            <XCircle className="h-6 w-6" aria-hidden="true" />
                        </div>
                        <p className="max-w-xl text-sm text-slate-700">
                            {ERROR_LEAD} ({errorMessage})
                        </p>
                        <div className="flex gap-2">
                            <Button data-testid="mydata-retry" onClick={runUpload}>
                                Retry
                            </Button>
                            <Button variant="outline" onClick={handleClose}>
                                Close
                            </Button>
                        </div>
                    </div>
                )}

                {phase === "result" && (
                    <div className="space-y-4">
                        <SummaryBanner rows={results} />
                        <div className="relative overflow-x-auto rounded-md border">
                            <Table data-testid="mydata-results-table">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="font-bold whitespace-nowrap">
                                            RESERVATION CODE
                                        </TableHead>
                                        <TableHead className="font-bold">STATUS</TableHead>
                                        <TableHead className="font-bold">TAX INVOICE</TableHead>
                                        <TableHead className="font-bold">RESERVATION INVOICE</TableHead>
                                        <TableHead className="font-bold">ERRORS</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {results.map((result) => (
                                        <ResultRow key={result.reservationCode} result={result} />
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                        <div className="flex justify-end">
                            <Button variant="outline" onClick={handleClose}>
                                Close
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default UploadToMydataDialog;
